import axios from "axios";

import { getBaseUrl } from "./base-url";
import { getApi } from "./get-api";
import { setPendingSampleAnalyse } from "./pending-sample-analyse";
import type { DatasourceDto } from "./generated/api-client";

/**
 * MOSA-908. Create (or return) the caller's read-only sample Sheet.
 *
 * The route postdates the generated client, so this module uses axios like
 * the rest of the Sheets wizard. A second call returns the same id. Guests
 * may call it. When the sample is not configured, the API returns 503 with
 * code `sample_sheet_unavailable`.
 */

export const SAMPLE_SHEET_UNAVAILABLE = "sample_sheet_unavailable";

export type SampleSheetDatasource = DatasourceDto & {
  isDemo?: boolean;
  readOnly?: boolean;
};

const authHeaders = (token: string | null) =>
  token ? { Authorization: `Bearer ${token}` } : {};

/** True when the API says the sample Sheet is not configured. */
export function isSampleSheetUnavailable(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const response = (error as { response?: { status?: number; data?: unknown } })
    .response;
  if (response?.status !== 503) return false;
  const data = response.data;
  if (!data || typeof data !== "object") return false;
  const code = (data as { code?: unknown }).code;
  return code === SAMPLE_SHEET_UNAVAILABLE;
}

/**
 * Create the caller's sample Sheet data source, or return the existing one.
 * Rejects with the raw axios error so callers can map typed codes.
 */
export async function createSampleSheet(
  token: string | null,
): Promise<SampleSheetDatasource> {
  const response = await axios.post<SampleSheetDatasource>(
    `${getBaseUrl()}/datasources/sample-sheet`,
    {},
    { headers: authHeaders(token) },
  );
  return response.data;
}

/**
 * Start analysis for a freshly created sample, right after create succeeds.
 *
 * Safer than starting from Data Sources on `?highlight=`: that path re-ran on
 * remount, and it treated any non-SUCCESS source (failed, running, Viewer)
 * as something to kick. Here we only start when the create body has no
 * attempts at all. Guests and Viewers never call this (callers gate first).
 * Failures are quiet: the Sample card still offers Retry.
 */
export async function startSampleSheetAnalysis(
  token: string | null,
  datasource: SampleSheetDatasource,
): Promise<void> {
  const id = datasource.id;
  if (!id) return;
  const attempts = datasource.analyzeAttempts ?? [];
  if (attempts.length > 0) return;

  try {
    const api = getApi({ token });
    const initial = await api.analysis.createAnalyzeAttempt(id, {
      async: true,
    });
    setPendingSampleAnalyse({
      datasourceId: id,
      attempt: initial.data,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Sample sheet analysis did not start", error);
    setPendingSampleAnalyse(null);
  }
}
